/**
 * Analyse locale de l'hygiene des mots de passe (entrees deja dechiffrees en
 * memoire pour l'utilisateur). Brique reutilisable par un futur Kopilvia
 * (`docs/ARCHITECTURE.md` §7) : detection de mots de passe faibles/reutilises
 * SANS jamais faire sortir de secret de l'appareil.
 *
 * Contraintes strictes (miroir de `test/no-leak.test.ts`) :
 *  - zero E/S, zero reseau, zero journalisation ;
 *  - le rapport produit ne contient JAMAIS de mot de passe en clair, seulement
 *    des identifiants d'entree et des indicateurs booleens/numeriques — sûr a
 *    afficher, journaliser au besoin cote appelant, ou transmettre a une UI.
 */
import { registrableDomain, siteHostname } from './domain.js';
import { InvalidVaultModelInputError } from './errors.js';

import { COMMON_PASSWORDS } from './common-passwords-data.js';
import { COMMON_WORDS_EN, COMMON_WORDS_FR } from './common-words-data.js';
import { FIRST_NAMES } from './first-names-data.js';
import { SURNAMES } from './surnames-data.js';

/** Poids (bits) accorde a chaque classe de caracteres presente dans le mot de passe. */
const CHARACTER_CLASSES: readonly RegExp[] = [
  /[a-z]/, // minuscules : +26
  /[A-Z]/, // majuscules : +26
  /[0-9]/, // chiffres : +10
  /[^a-zA-Z0-9]/, // symboles/unicode : +32 (estimation prudente)
];
const CHARACTER_CLASS_POOL_SIZES: readonly number[] = [26, 26, 10, 32];

/** Borne par les classes de caracteres : `longueur * log2(somme des tailles de classes presentes)`. */
function characterClassBits(password: string): number {
  let poolSize = 0;
  for (let i = 0; i < CHARACTER_CLASSES.length; i++) {
    if (CHARACTER_CLASSES[i]!.test(password)) poolSize += CHARACTER_CLASS_POOL_SIZES[i]!;
  }
  if (poolSize === 0) return 0;
  return [...password].length * Math.log2(poolSize);
}

/**
 * Rang (1 = le plus courant) de chaque mot connu : mot de passe courant, prenom (« julien », « sebastien »), nom de
 * famille (« lefebvre », « fontaine ») ou mot courant du francais et de l'anglais (« grenouille », « sunshine »), le
 * meilleur des rangs. Construit au premier usage.
 */
let knownWordRanks: Map<string, number> | null = null;
function knownWords(): Map<string, number> {
  if (knownWordRanks === null) {
    knownWordRanks = new Map();
    for (const list of [COMMON_PASSWORDS, FIRST_NAMES, SURNAMES, COMMON_WORDS_FR, COMMON_WORDS_EN]) {
      list.split(' ').forEach((word, index) => {
        if ((knownWordRanks!.get(word) ?? Infinity) > index + 1) knownWordRanks!.set(word, index + 1);
      });
    }
  }
  return knownWordRanks;
}
/** Les listes sont sans accents : « Sébastien » se lit `sebastien`. */
function withoutAccents(lowered: string): string {
  return lowered.normalize('NFD').replace(/\p{M}/gu, '');
}
/**
 * Mots propres a l'entree en cours d'estimation (identifiant, nom du site), rang 1, 2... dans l'ordre donne, comme
 * `user_inputs` de zxcvbn : l'attaquant qui vise ce compte les essaie en premier. Le temps d'un appel seulement.
 */
let entryWordRanks: ReadonlyMap<string, number> = new Map();
function commonPasswordRank(lowered: string): number | undefined {
  const word = withoutAccents(lowered);
  const known = knownWords().get(word);
  const entry = entryWordRanks.get(word);
  return known === undefined ? entry : Math.min(known, entry ?? Infinity);
}

/** Rangees de clavier (QWERTY, AZERTY, chiffres) : « qwerty », « azerty », « poiuy » sont des suites, pas du hasard. */
const KEYBOARD_ROWS: readonly string[] = ['1234567890', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm', 'azertyuiop', 'qsdfghjklm', 'wxcvbn'];

/** Substitutions « leet » les plus courantes, lues a l'envers (`p@ssw0rd` -> `password`). `1` vaut `i` ou `l`. */
const LEET: Readonly<Record<string, readonly string[]>> = {
  '4': ['a'], '@': ['a'], '8': ['b'], '3': ['e'], '6': ['g'], '9': ['g'], '1': ['i', 'l'], '!': ['i'],
  '0': ['o'], '5': ['s'], '$': ['s'], '7': ['t'], '+': ['t'], '2': ['z'],
};

/** Taille de la classe d'un caractere (meme decoupage que `CHARACTER_CLASSES`). */
function classSize(char: string): number {
  if (/[a-z]/.test(char)) return 26;
  if (/[A-Z]/.test(char)) return 26;
  if (/[0-9]/.test(char)) return 10;
  return 32;
}

/** Pas le plus grand d'une suite (« afkpuz » : +5), comme `MAX_DELTA` de zxcvbn. */
const SEQUENCE_MAX_STEP = 5;
/**
 * Suite sans hasard (minuscules) : un meme caractere repete (« aaaa »), une suite de points de code a pas constant
 * de +-1 a +-5 (« abcdef », « 13579 », « 4321 », « adgjmp », « zupkfa »), un trongon de rangee de clavier (« qwerty »,
 * « poiuy »). Bits : le premier caractere, le sens, la longueur. `undefined` si ce n'en est pas une.
 */
function sequenceBits(lowered: string): number | undefined {
  const chars = [...lowered];
  if (chars.length < 3) return undefined;
  const lengthBits = Math.log2(chars.length);
  if (chars.every((char) => char === chars[0])) return Math.log2(classSize(chars[0]!)) + lengthBits;
  const step = chars[1]!.codePointAt(0)! - chars[0]!.codePointAt(0)!;
  if (Math.abs(step) <= SEQUENCE_MAX_STEP && chars.every((char, i) => i === 0 || char.codePointAt(0)! - chars[i - 1]!.codePointAt(0)! === step)) {
    return Math.log2(classSize(chars[0]!)) + 1 + lengthBits;
  }
  if (chars.length >= 4) {
    for (const row of KEYBOARD_ROWS) {
      if (row.includes(lowered) || [...row].reverse().join('').includes(lowered)) {
        return Math.log2(KEYBOARD_ROWS.length * 10) + 1 + lengthBits;
      }
    }
  }
  return undefined;
}

/**
 * Claviers QWERTY, AZERTY, QWERTZ : quatre rangees de touches a partir de « 1 », chaque touche ecrite « sans Maj »
 * puis « avec Maj » (en AZERTY, les chiffres sont la position Maj). Une rangee est decalee d'une demi-touche vers la
 * droite par rapport a celle du dessus, comme sur un vrai clavier : « q » touche « 1 », « 2 », « w » et « a ».
 */
const KEYBOARD_LAYOUTS: readonly (readonly (readonly [string, string])[])[] = [
  [['1234567890-=', '!@#$%^&*()_+'], ['qwertyuiop[]', 'QWERTYUIOP{}'], ["asdfghjkl;'", 'ASDFGHJKL:"'], ['zxcvbnm,./', 'ZXCVBNM<>?']],
  [['&é"\'(-è_çà)=', '1234567890°+'], ['azertyuiop^$', 'AZERTYUIOP¨£'], ['qsdfghjklmù*', 'QSDFGHJKLM%µ'], ['wxcvbn,;:!', 'WXCVBN?./§']],
  [['1234567890ß', '!"§$%&/()=?'], ['qwertzuiopü+', 'QWERTZUIOPÜ*'], ['asdfghjklöä#', "ASDFGHJKLÖÄ'"], ['yxcvbnm,.-', 'YXCVBNM;:_']],
];
/** Voisines d'une touche (rangee, colonne) : gauche et droite, les deux du dessus (colonne, colonne + 1), les deux du dessous (colonne - 1, colonne). */
const KEY_NEIGHBOURS: readonly (readonly [number, number])[] = [[0, -1], [0, 1], [-1, 0], [-1, 1], [1, -1], [1, 0]];
/**
 * Paves numeriques, PC puis Mac, comme zxcvbn (`keypad`, `mac_keypad`) : touches alignees en colonnes (« 7 » au-dessus
 * de « 4 »), une espace pour une case vide (le « 0 » double est lu sous le « 2 », le « + » double a cote du « 9 »).
 * Aucune touche Maj.
 */
const KEYPAD_LAYOUTS: readonly (readonly (readonly [string, string])[])[] = [
  [[' /*-', ''], ['789+', ''], ['456', ''], ['123', ''], [' 0.', '']],
  [[' =/*', ''], ['789-', ''], ['456+', ''], ['123', ''], [' 0.', '']],
];
/** Voisines d'une touche de pave : les huit touches qui l'entourent, diagonales comprises. */
const KEYPAD_NEIGHBOURS: readonly (readonly [number, number])[] = [[0, -1], [0, 1], [-1, -1], [-1, 0], [-1, 1], [1, -1], [1, 0], [1, 1]];
/** Pour un clavier : la touche de chaque caractere, s'il est en position Maj, le nombre de touches, le degre moyen. */
function keyGraph(rows: readonly (readonly [string, string])[], neighbours: readonly (readonly [number, number])[]) {
  const keys = new Map<string, { readonly row: number; readonly column: number; readonly shifted: boolean }>();
  rows.forEach(([plain, shifted], row) => {
    [...plain].forEach((char, column) => char !== ' ' && keys.set(char, { row, column, shifted: false }));
    [...shifted].forEach((char, column) => keys.set(char, { row, column, shifted: true }));
  });
  const keyCount = rows.reduce((sum, [plain]) => sum + [...plain].filter((char) => char !== ' ').length, 0);
  let degrees = 0;
  rows.forEach(([plain], row) => {
    [...plain].forEach((char, column) => {
      if (char === ' ') return;
      for (const [dr, dc] of neighbours) {
        const neighbourKey = [...(rows[row + dr]?.[0] ?? '')][column + dc];
        if (neighbourKey !== undefined && neighbourKey !== ' ') degrees++;
      }
    });
  });
  return { keys, keyCount, averageDegree: degrees / keyCount, neighbours };
}
/** Les claviers, puis les paves : chaque famille compte a part le choix de son clavier. */
const KEY_GRAPH_FAMILIES = [
  KEYBOARD_LAYOUTS.map((rows) => keyGraph(rows, KEY_NEIGHBOURS)),
  KEYPAD_LAYOUTS.map((rows) => keyGraph(rows, KEYPAD_NEIGHBOURS)),
];
/**
 * Motif de clavier (« 1qaz2wsx », « zaq1 », « mju7 », « 1q2w3e4r ») ou de pave numerique (« 7412369 », « 0258 ») :
 * chaque touche voisine de la precedente, sur un meme clavier. Comme zxcvbn (`spatial_match`) : la touche de depart, le nombre de virages et le degre moyen du clavier,
 * plus les touches tapees avec Maj (« !QAZ ») et le choix du clavier. `undefined` si ce n'en est pas un.
 */
function spatialBits(segment: string): number | undefined {
  const chars = [...segment];
  // A partir de 4 touches, comme les rangees de clavier : trois touches voisines se croisent aussi par hasard.
  if (chars.length < 4) return undefined;
  let best = Infinity;
  for (const graphs of KEY_GRAPH_FAMILIES) {
    for (const { keys, keyCount, averageDegree, neighbours } of graphs) {
      const path = chars.map((char) => keys.get(char));
      if (path.some((key) => key === undefined)) continue;
      let turns = 0;
      let direction = '';
      let chained = true;
      for (let i = 1; i < path.length && chained; i++) {
        const dr = path[i]!.row - path[i - 1]!.row;
        const dc = path[i]!.column - path[i - 1]!.column;
        chained = neighbours.some(([r, c]) => r === dr && c === dc);
        if (`${dr},${dc}` !== direction) turns++;
        direction = `${dr},${dc}`;
      }
      if (!chained) continue;
      let guesses = 0;
      for (let length = 2; length <= chars.length; length++) {
        for (let turn = 1; turn <= Math.min(turns, length - 1); turn++) guesses += binomial(length - 1, turn - 1) * keyCount * averageDegree ** turn;
      }
      const shifted = path.filter((key) => key!.shifted).length;
      best = Math.min(best, Math.log2(guesses) + shiftBits(shifted, chars.length - shifted) + Math.log2(graphs.length));
    }
  }
  return best === Infinity ? undefined : best;
}

/** Bits de la casse d'un mot reconnu : rien en minuscules, 1 bit tout en capitales ou capitale initiale, sinon les combinaisons. */
function caseBits(word: string): number {
  // Lettres Unicode : « Élodie » a une capitale initiale, comme « Elodie ».
  const upper = [...word].filter((char) => /\p{Lu}/u.test(char)).length;
  const lower = [...word].filter((char) => /\p{Ll}/u.test(char)).length;
  if (upper === 1 && lower > 0 && /^\p{Lu}/u.test(word)) return 1;
  return shiftBits(upper, lower);
}
/** Bits des touches tapees avec Maj (capitales, symboles) : rien sans Maj, 1 bit tout avec Maj, sinon les combinaisons. */
function shiftBits(shifted: number, plain: number): number {
  if (shifted === 0) return 0;
  if (plain === 0) return 1;
  let combinations = 0;
  for (let i = 1; i <= Math.min(shifted, plain); i++) combinations += binomial(shifted + plain, i);
  return Math.log2(combinations);
}

function binomial(n: number, k: number): number {
  let result = 1;
  for (let i = 1; i <= k; i++) result = (result * (n - k + i)) / i;
  return result;
}

/** Lectures « leet » inversees d'un mot (au plus 16), avec le nombre de caracteres substitues. */
function unleetVariants(lowered: string): { readonly word: string; readonly substitutions: number }[] {
  let variants = [{ word: '', substitutions: 0 }];
  for (const char of lowered) {
    const readings = LEET[char];
    if (readings === undefined) {
      variants = variants.map((variant) => ({ word: variant.word + char, substitutions: variant.substitutions }));
      continue;
    }
    variants = variants.flatMap((variant) => readings.map((reading) => ({ word: variant.word + reading, substitutions: variant.substitutions + 1 })));
    if (variants.length > 16) return [];
  }
  return variants.filter((variant) => variant.substitutions > 0);
}

function reversed(text: string): string {
  return [...text].reverse().join('');
}
/** Bits d'un mot connu en minuscules, sans sa casse : son rang parmi les mots de passe courants, ses substitutions « leet ». */
function loweredDictionaryBits(lowered: string): number | undefined {
  const rank = commonPasswordRank(lowered);
  // « pa55word » est lui-meme courant, mais « password » + deux substitutions l'est davantage : le plus bas des deux.
  let best = rank === undefined ? undefined : Math.log2(rank);
  for (const variant of unleetVariants(lowered)) {
    const variantRank = commonPasswordRank(variant.word);
    if (variantRank === undefined) continue;
    const bits = Math.log2(variantRank) + variant.substitutions;
    if (best === undefined || bits < best) best = bits;
  }
  return best;
}
/**
 * Bits d'un mot connu : son rang, sa casse, ses substitutions « leet » ; ecrit a l'envers (« neitsabes », « yeknom »),
 * 1 bit de plus, comme zxcvbn ; sa casse se lit telle qu'ecrite ou a l'endroit (« neitsabeS » = « Sebastien » : une
 * capitale initiale), la moins chere des deux.
 */
function dictionaryBits(word: string): number | undefined {
  const lowered = word.toLowerCase();
  const forward = loweredDictionaryBits(lowered);
  const backward = loweredDictionaryBits(reversed(lowered));
  const best = Math.min(
    forward === undefined ? Infinity : forward + caseBits(word),
    backward === undefined ? Infinity : backward + 1 + Math.min(caseBits(word), caseBits(reversed(word))),
  );
  return best === Infinity ? undefined : best;
}

/**
 * Bits d'un ornement (prefixe ou suffixe d'un mot connu), par trongons de chiffres et de symboles (« 1234! » =
 * « 1234 » + « ! ») : une annee, une suite, sinon ses classes de caracteres.
 */
function decorationBits(decoration: string): number {
  let bits = 0;
  for (const run of decoration.match(/[0-9]+|[^0-9]+/gu) ?? []) {
    // Une annee de naissance ou de creation : ~150 valeurs plausibles, pas 10^4.
    if (/^(19|20)\d\d$/u.test(run)) bits += Math.log2(150);
    else bits += Math.min(sequenceBits(run.toLowerCase()) ?? Infinity, characterClassBits(run));
  }
  return bits;
}

/** Annee plausible : quatre chiffres de 1900 a 2099 (~150 valeurs, comme une annee en ornement), ou deux chiffres. */
function dateYearBits(year: string): number | undefined {
  if (year.length === 2) return Math.log2(100);
  if (year.length === 4 && /^(19|20)\d\d$/u.test(year)) return Math.log2(150);
  return undefined;
}
/** Jour 1 a 31, ordinal compris (« 1er », « 4th ») : un ordinal n'est jamais un mois ni une annee. */
function isDay(value: string): boolean {
  const day = value.replace(/^(\d{1,2})(?:er|st|nd|rd|th)$/iu, '$1');
  return day.length <= 2 && Number(day) >= 1 && Number(day) <= 31;
}
function isMonth(value: string): boolean {
  return value.length <= 2 && Number(value) >= 1 && Number(value) <= 12;
}
/** Mois en lettres, francais et anglais, avec et sans accents : leur numero. */
const MONTH_NAMES: ReadonlyMap<string, number> = new Map([
  ...['janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin', 'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'].map((name, i) => [name, i + 1] as const),
  ...['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'].map((name, i) => [name, i + 1] as const),
  ['février', 2], ['août', 8], ['décembre', 12],
]);
/** Mois abreges, francais et anglais (« 14jan89 », « 3-sept-2001 », « 12déc1990 ») : leur numero. */
const MONTH_ABBREVIATIONS: ReadonlyMap<string, number> = new Map([
  ['jan', 1], ['janv', 1], ['feb', 2], ['fev', 2], ['fév', 2], ['fevr', 2], ['févr', 2], ['mar', 3], ['apr', 4], ['avr', 4],
  ['jun', 6], ['jul', 7], ['juil', 7], ['aug', 8], ['sep', 9], ['sept', 9], ['oct', 10], ['nov', 11], ['dec', 12], ['déc', 12],
]);
/** Mots du calendrier qu'un mot de passe accole a une annee (« Septembre2024! », « Ete2023 ») : mois et saisons, francais et anglais. */
const CALENDAR_WORDS: ReadonlySet<string> = new Set([
  ...MONTH_NAMES.keys(),
  'printemps', 'ete', 'été', 'automne', 'hiver', 'spring', 'summer', 'autumn', 'fall', 'winter',
]);
/** Bits d'un mot du calendrier (« Septembre », « hiver ») : sa place dans la liste, sa casse. `undefined` sinon. */
function calendarWordBits(word: string): number | undefined {
  return CALENDAR_WORDS.has(word.toLowerCase()) ? Math.log2(CALENDAR_WORDS.size) + caseBits(word) : undefined;
}
/** Bits d'un jour, d'un mois et d'une annee dans cet ordre-ci ou l'un des deux autres (JMA, MJA, AMJ). */
function orderedDateBits(first: string, second: string, third: string): number | undefined {
  const yearLast = dateYearBits(third);
  const yearFirst = dateYearBits(first);
  if (yearLast !== undefined && ((isDay(first) && isMonth(second)) || (isMonth(first) && isDay(second)))) return Math.log2(366) + yearLast;
  if (yearFirst !== undefined && isMonth(second) && isDay(third)) return Math.log2(366) + yearFirst;
  return undefined;
}
/**
 * Bits d'une date au mois ecrit en lettres (« 14juillet1989 », « 14 juillet 89 », « July-4-1990 », « 1989.juillet.14 »),
 * abrege (« 14jan89 ») ou au jour ordinal (« 1er-août-1989 », « July4th1990 ») : la date comme en chiffres, plus 1 bit
 * pour la langue et la casse du mois, 1 bit pour l'abreviation, 1 bit pour l'ordinal. `undefined` si ce n'en est pas une.
 */
function monthNameDateBits(candidate: string): number | undefined {
  const parts = /^(\d{1,2}(?:er|st|nd|rd|th)|\d{1,4}|\p{L}+)([ /\\._-]?)(\d{1,2}(?:er|st|nd|rd|th)|\d{1,4}|\p{L}+)\2(\d{1,2}(?:er|st|nd|rd|th)|\d{1,4}|\p{L}+)$/iu.exec(candidate);
  if (parts === null) return undefined;
  const fields = [parts[1]!, parts[3]!, parts[4]!];
  const named = fields.filter((field) => /^\p{L}+$/u.test(field));
  if (named.length !== 1) return undefined;
  const lowered = named[0]!.toLowerCase();
  const month = MONTH_NAMES.get(lowered) ?? MONTH_ABBREVIATIONS.get(lowered);
  if (month === undefined) return undefined;
  const [first, second, third] = fields.map((field) => (field === named[0] ? String(month) : field)) as [string, string, string];
  const bits = orderedDateBits(first, second, third);
  if (bits === undefined) return undefined;
  return bits + 1 + caseBits(named[0]!) + (MONTH_NAMES.has(lowered) ? 0 : 1) + (fields.some((field) => /\d\p{L}/u.test(field)) ? 1 : 0) + (parts[2]!.length > 0 ? 2 : 0);
}
/**
 * Bits d'une date (« 14071989 », « 14/07/89 », « 1989-07-14 ») : jour et mois (366 valeurs), l'annee, 2 bits pour le
 * separateur. Ordres jour-mois-annee, mois-jour-annee et annee-mois-jour ; sans separateur, 6 ou 8 chiffres.
 * `undefined` si ce n'en est pas une.
 */
function dateBits(candidate: string): number | undefined {
  const separated = /^(\d{1,4})([ /\\._-])(\d{1,2})\2(\d{1,4})$/u.exec(candidate);
  let splits: (readonly [string, string, string])[];
  if (separated !== null) {
    splits = [[separated[1]!, separated[3]!, separated[4]!]];
  } else if (/^\d{8}$/u.test(candidate)) {
    splits = [[candidate.slice(0, 2), candidate.slice(2, 4), candidate.slice(4)], [candidate.slice(0, 4), candidate.slice(4, 6), candidate.slice(6)]];
  } else if (/^\d{6}$/u.test(candidate)) {
    splits = [[candidate.slice(0, 2), candidate.slice(2, 4), candidate.slice(4)]];
  } else {
    return monthNameDateBits(candidate);
  }
  let best: number | undefined;
  for (const [first, second, third] of splits) {
    const dayMonthYear = orderedDateBits(first, second, third);
    if (dayMonthYear === undefined) continue;
    const bits = dayMonthYear + (separated !== null ? 2 : 0);
    if (best === undefined || bits < best) best = bits;
  }
  return best;
}
/** Au-dela, une date ne pese plus sur l'estimation : la recherche s'arrete (cout borne). */
const DATE_SEARCH_MAX_LENGTH = 64;
/** Une date tient en 19 caracteres au plus (« 30th September 1989 ») : au-dela, seuls un mot ou une suite peuvent s'allonger. */
const DATE_MAX_LENGTH = 19;
/**
 * Borne par les dates : une date n'importe ou dans le mot de passe (« 14/07/1989 », « julie14/07/89! »,
 * « Klavox14071989 »), plus ce qui l'entoure estime sans date. `Infinity` si aucune date n'est reconnue.
 */
function dateSplitBits(password: string): number {
  const chars = [...password];
  if (chars.length > DATE_SEARCH_MAX_LENGTH) return Infinity;
  const sideBits = (side: string): number => (side.length === 0 ? 0 : Math.min(characterClassBits(side), patternBits(side)));
  let best = Infinity;
  for (let start = 0; start < chars.length; start++) {
    for (let end = start + 6; end <= Math.min(start + DATE_MAX_LENGTH, chars.length); end++) {
      const date = dateBits(chars.slice(start, end).join(''));
      if (date === undefined) continue;
      best = Math.min(best, sideBits(chars.slice(0, start).join('')) + date + sideBits(chars.slice(end).join('')));
    }
  }
  return best;
}
/**
 * Debuts des mots connus (mots de passe courants, prenoms, noms de famille, mots courants), a l'endroit et a l'envers : un trongon qui n'en est pas un
 * ne deviendra pas un mot en s'allongeant.
 */
let knownWordsSorted: string[] | null = null;
function couldBecomeWord(lowered: string): boolean {
  // Liste triee plutot qu'ensemble de tous les debuts : un trongon debute un mot si le premier mot qui ne le precede
  // pas commence par lui (recherche dichotomique) ; deux fois plus rapide a construire, memes reponses.
  if (knownWordsSorted === null) knownWordsSorted = [...knownWords().keys()].flatMap((word) => [word, reversed(word)]).sort();
  const sorted = knownWordsSorted;
  const isPrefix = (start: string): boolean => {
    let low = 0;
    let high = sorted.length;
    while (low < high) {
      const middle = (low + high) >>> 1;
      if (sorted[middle]! < start) low = middle + 1;
      else high = middle;
    }
    return low < sorted.length && sorted[low]!.startsWith(start);
  };
  const begins = (start: string): boolean =>
    isPrefix(start) || [...entryWordRanks.keys()].some((word) => word.startsWith(start) || reversed(word).startsWith(start));
  return begins(withoutAccents(lowered)) || unleetVariants(lowered).some((variant) => begins(variant.word));
}
/** Les `length` caracteres a partir de `other` repetent ceux a partir de `from`. */
function sameBlock(chars: readonly string[], from: number, other: number, length: number): boolean {
  if (other + length > chars.length) return false;
  for (let i = 0; i < length; i++) if (chars[from + i] !== chars[other + i]) return false;
  return true;
}
/**
 * Borne par les mots accoles : le meilleur decoupage du mot de passe en trongons reconnus (« dragon » + « monkey »,
 * « Sunshine » + « 2024 » + « Princess », « Hn5$ » deux fois + « 1 », « !QAZ » + « 2wsx ») — mot connu de 4 lettres et plus, suite, motif de clavier, annee,
 * date, bloc repete —, le reste compte au prix de chaque caractere dans les classes du mot de passe entier (jamais
 * moins cher qu'il ne l'etait). Sans trongon reconnu, ce sont les classes de caracteres.
 */
function segmentationBits(password: string): number {
  const chars = [...password];
  if (chars.length > DATE_SEARCH_MAX_LENGTH) return Infinity;
  const charBits = characterClassBits(password) / chars.length;
  // reach[i] : le moins cher des decoupages des i premiers caracteres (caractere seul ou trongon reconnu).
  const reach: number[] = new Array<number>(chars.length + 1).fill(Infinity);
  reach[0] = 0;
  // Force de chaque bloc repete deja estime, le temps de cet appel seulement (jamais de secret garde au-dela).
  const blockBits = new Map<string, number>();
  for (let from = 0; from < chars.length; from++) {
    reach[from + 1] = Math.min(reach[from + 1]!, reach[from]! + charBits);
    let word = true;
    let sequence = true;
    let spatial = true;
    for (let end = from + 3; end <= chars.length && (word || sequence || spatial || end - from <= DATE_MAX_LENGTH); end++) {
      const segment = chars.slice(from, end).join('');
      const lowered = segment.toLowerCase();
      word &&= couldBecomeWord(lowered);
      // Repetition, pas constant, trongon de rangee : une suite raccourcie reste une suite (a partir de 4, rangees comprises).
      const sequenceBitsHere = sequence ? sequenceBits(lowered) : undefined;
      if (sequenceBitsHere === undefined && segment.length >= 4) sequence = false;
      // Motif de clavier : un chemin rompu ne se renoue pas en s'allongeant.
      const spatialBitsHere = spatial ? spatialBits(segment) : undefined;
      if (spatialBitsHere === undefined && segment.length >= 4) spatial = false;
      const token = Math.min(
        word && segment.length >= 4 ? (dictionaryBits(segment) ?? Infinity) : Infinity,
        sequenceBitsHere ?? Infinity,
        spatialBitsHere ?? Infinity,
        /^(19|20)\d\d$/u.test(segment) ? Math.log2(150) : Infinity,
        calendarWordBits(segment) ?? Infinity,
        end - from <= DATE_MAX_LENGTH ? (dateBits(segment) ?? Infinity) : Infinity,
      );
      reach[end] = Math.min(reach[end]!, reach[from]! + token);
    }
    // Bloc repete n'importe ou (« x7Kp2x7Kp2x7Kp2! », « Hn5$Hn5$1 »), comme zxcvbn : le bloc estime seul, plus le
    // nombre de repetitions, comme un mot de passe entierement repete. Le bloc n'est jamais lui-meme une repetition
    // (« abab » se lit « ab » deux fois) : la recursion ne porte que sur des blocs de moitie au plus.
    for (let length = 1; from + 2 * length <= chars.length; length++) {
      let count = 1;
      while (sameBlock(chars, from, from + count * length, length)) {
        count++;
        if (count * length < 4) continue;
        const block = chars.slice(from, from + length).join('');
        if (/^(.+)\1+$/su.test(block)) break;
        let bits = blockBits.get(block);
        if (bits === undefined) blockBits.set(block, (bits = estimateBits(block)));
        reach[from + count * length] = Math.min(reach[from + count * length]!, reach[from]! + bits + Math.log2(count));
      }
    }
  }
  return reach[chars.length]!;
}
/**
 * Borne par les motifs : le mot de passe entier ou son coeur (sans ornements non alphabetiques autour) est un mot de
 * passe courant, une suite, ou un bloc repete. `Infinity` si aucun motif n'est reconnu.
 */
function patternBits(password: string): number {
  let best = Math.min(dictionaryBits(password) ?? Infinity, sequenceBits(password.toLowerCase()) ?? Infinity);
  // « abcabc », « azerty123azerty123 » : le bloc, plus le nombre de repetitions.
  const repeated = /^(.+?)\1+$/su.exec(password);
  if (repeated !== null && repeated[1]!.length < password.length) {
    best = Math.min(best, estimateBits(repeated[1]!) + Math.log2(password.length / repeated[1]!.length));
  }
  // « Admin1234 », « P@ssw0rd! », « 2024dragon », « Élodie1990! » : un mot connu entoure d'ornements sans lettre
  // (une lettre accentuee est une lettre, jamais un ornement).
  const parts = /^([^\p{L}]*)(.*?)([^\p{L}]*)$/su.exec(password)!;
  const [, prefix, core, suffix] = parts as unknown as [string, string, string, string];
  if (core.length > 0 && core !== password) {
    const coreBits = Math.min(dictionaryBits(core) ?? Infinity, sequenceBits(core.toLowerCase()) ?? Infinity);
    best = Math.min(best, coreBits + decorationBits(prefix) + decorationBits(suffix));
  }
  return best;
}

/**
 * Estimation PRUDENTE de l'entropie d'un mot de passe existant : la plus basse de trois bornes.
 *  - classes de caracteres : `longueur * log2(somme des tailles de classes presentes)` ;
 *  - motifs : un mot de passe courant, un prenom, un nom de famille ou un mot courant (rang dans `common-passwords-data.ts`,
 *    `first-names-data.ts`, `surnames-data.ts` ou `common-words-data.ts`,
 *    casse, accents, substitutions « leet » et ecriture a l'envers compris), une suite (« abcdef », « 4321 », « azerty »), un bloc repete, un mot connu entoure d'ornements
 *    (« Admin1234 », « P@ssw0rd! », « dragon2024 ») ;
 *  - dates : une date de naissance ou d'anniversaire n'importe ou (« 14/07/1989 », « julie140789 », « 14 juillet 1989 ») ;
 *  - mots accoles : le meilleur decoupage en mots connus, suites, motifs de clavier, annees, dates, mois et saisons, blocs repetes (« dragonmonkey », « !QAZ2wsx »,
 *    « Septembre2024! », « x7Kp2x7Kp2x7Kp2! »).
 * `entryWords` : mots propres a l'entree (`passwordEntryWords` : nom, identifiant, nom du site), lus comme des mots connus
 * de premier rang (« jdupont2024! » pour `jdupont@…`, « Netflix2024! » pour `netflix.com`), comme `user_inputs` de zxcvbn.
 * Meme approche que zxcvbn (Dropbox), en plus simple.
 * Ne remplace pas un audit, seulement un signal local rapide, sans reseau.
 */
export function estimatePasswordStrengthBits(password: string, entryWords: readonly string[] = []): number {
  if (typeof password !== 'string') {
    throw new InvalidVaultModelInputError('password: chaine attendue');
  }
  if (!Array.isArray(entryWords) || entryWords.some((word) => typeof word !== 'string')) {
    throw new InvalidVaultModelInputError('entryWords: tableau de chaines attendu');
  }
  const previous = entryWordRanks;
  const ranks = new Map<string, number>();
  // Comme Bitwarden : en minuscules, coupes sur tout ce qui n'est ni lettre ni chiffre, 3 caracteres au moins.
  for (const word of entryWords.flatMap((input) => withoutAccents(input.toLowerCase()).split(/[^\p{L}\p{N}]+/u))) {
    if (word.length >= 3 && !ranks.has(word)) ranks.set(word, ranks.size + 1);
  }
  entryWordRanks = ranks;
  try {
    return estimateBits(password);
  } finally {
    entryWordRanks = previous;
  }
}
function estimateBits(password: string): number {
  if (password.length === 0) return 0;
  return Math.max(0, Math.min(characterClassBits(password), patternBits(password), dateSplitBits(password), segmentationBits(password)));
}

/**
 * Mots que l'entree donne a qui vise ce compte : son nom affiche (« Doctolib », souvent le seul indice d'une entree
 * sans adresse), son identifiant (sans le domaine d'une adresse e-mail, comme le rapport « mots de passe faibles » de
 * Bitwarden) et le nom de chacun de ses sites (« netflix » pour `www.netflix.com`).
 */
export function passwordEntryWords(secrets: { readonly name?: unknown; readonly username?: unknown; readonly uris?: unknown }): string[] {
  const words: string[] = [];
  if (typeof secrets.name === 'string') words.push(secrets.name);
  if (typeof secrets.username === 'string') words.push(secrets.username.split('@')[0]!);
  for (const uri of Array.isArray(secrets.uris) ? secrets.uris : []) {
    const host = typeof uri === 'string' ? siteHostname(uri) : null;
    const site = host === null ? null : registrableDomain(host);
    if (site !== null) words.push(site.split('.')[0]!);
  }
  return words;
}

/** Sous le seuil, un mot de passe est signale comme faible (borne prudente). */
export const WEAK_PASSWORD_BITS_THRESHOLD = 40;

export interface HygieneVaultItem {
  readonly id: string;
  readonly secrets: { readonly password?: string; readonly [key: string]: unknown };
}

/** Constat d'hygiene pour une entree : jamais de mot de passe en clair dedans. */
export interface PasswordHygieneFinding {
  readonly itemId: string;
  readonly weak: boolean;
  /** `true` si au moins une autre entree du lot partage le meme mot de passe. */
  readonly reused: boolean;
  readonly estimatedBits: number;
}

export interface PasswordHygieneReport {
  readonly findings: readonly PasswordHygieneFinding[];
  /** Sous-ensemble de `findings` ou `weak` ou `reused` est vrai. */
  readonly issues: readonly PasswordHygieneFinding[];
}

/**
 * Analyse un lot d'entrees DEJA DECHIFFREES (en memoire, apres deverrouillage
 * local du coffre) : mots de passe faibles et mots de passe reutilises entre
 * entrees. Les entrees sans mot de passe (notes, cartes...) sont ignorees.
 *
 * @param items entrees dechiffrees (`VaultItem`-compatibles) — jamais une
 *   forme chiffree/synchronisable : cette fonction ne doit tourner que
 *   localement, apres deverrouillage, jamais cote serveur.
 * @param accountWords mots du titulaire du coffre, communs a toutes les entrees
 *   (`passwordEntryWords({ username: e-mail du compte })`) : « ana.dupont2024 »
 *   est faible partout, pas seulement sur l'entree dont c'est l'identifiant.
 */
export function analyzePasswordHygiene(items: readonly HygieneVaultItem[], accountWords: readonly string[] = []): PasswordHygieneReport {
  if (!Array.isArray(items)) {
    throw new InvalidVaultModelInputError('items: tableau attendu');
  }

  const occurrences = new Map<string, number>();
  for (const item of items) {
    const password = item?.secrets?.password;
    if (typeof password !== 'string' || password.length === 0) continue;
    occurrences.set(password, (occurrences.get(password) ?? 0) + 1);
  }

  const findings: PasswordHygieneFinding[] = [];
  for (const item of items) {
    if (item === null || typeof item !== 'object' || typeof item.id !== 'string' || item.id.length === 0) {
      throw new InvalidVaultModelInputError('item.id: chaine non vide attendue');
    }
    const password = item.secrets?.password;
    if (typeof password !== 'string' || password.length === 0) continue;
    const estimatedBits = estimatePasswordStrengthBits(password, [...passwordEntryWords(item.secrets), ...accountWords]);
    findings.push({
      itemId: item.id,
      weak: estimatedBits < WEAK_PASSWORD_BITS_THRESHOLD,
      reused: (occurrences.get(password) ?? 0) > 1,
      estimatedBits,
    });
  }

  return { findings, issues: findings.filter((f) => f.weak || f.reused) };
}
